import { useState, useEffect } from "react";
import { loadGTM } from "@/lib/gtm";

const STORAGE_KEY = "cookie_consent";

export type ConsentValue = "accepted" | "declined";

export function getStoredConsent(): ConsentValue | null {
  try {
    const v = localStorage.getItem(STORAGE_KEY);
    if (v === "accepted" || v === "declined") return v;
  } catch {
    // ignore
  }
  return null;
}

function setStoredConsent(value: ConsentValue): void {
  try {
    localStorage.setItem(STORAGE_KEY, value);
  } catch {
    // ignore
  }
}

export function CookieConsentBanner() {
  const [visible, setVisible] = useState(false);

  useEffect(() => {
    const stored = getStoredConsent();
    if (stored === "accepted") {
      loadGTM();
    } else if (stored === null) {
      setVisible(true);
    }
  }, []);

  useEffect(() => {
    const onReopenRequest = () => setVisible(true);
    window.addEventListener("open-cookie-preferences", onReopenRequest);
    return () => window.removeEventListener("open-cookie-preferences", onReopenRequest);
  }, []);

  function handleAccept() {
    setStoredConsent("accepted");
    setVisible(false);
    loadGTM();
  }

  function handleDecline() {
    const wasAccepted = getStoredConsent() === "accepted";
    setStoredConsent("declined");
    setVisible(false);
    if (wasAccepted && document.getElementById("gtm-script")) {
      window.location.reload();
    }
  }

  if (!visible) return null;

  return (
    <div
      role="dialog"
      aria-label="Cookie consent"
      style={{
        position: "fixed",
        bottom: 0,
        left: 0,
        right: 0,
        zIndex: 9999,
        background: "#fff",
        borderTop: "1px solid #e5e7eb",
        boxShadow: "0 -2px 12px rgba(0,0,0,0.08)",
        padding: "1rem 1.5rem",
      }}
    >
      <div
        style={{
          maxWidth: "64rem",
          margin: "0 auto",
          display: "flex",
          flexWrap: "wrap",
          alignItems: "center",
          gap: "1rem",
        }}
      >
        <p style={{ flex: "1 1 20rem", fontSize: "0.875rem", color: "#374151", margin: 0 }}>
          We use cookies to keep your account secure and to analyze usage so we can improve QuoTrak. Accepting allows us to use analytics cookies. You can decline non-essential cookies.{" "}
          <a href="/privacy" style={{ color: "#1d4ed8", textDecoration: "underline" }}>
            Privacy Policy
          </a>
        </p>
        <div style={{ display: "flex", gap: "0.75rem", flexShrink: 0 }}>
          <button
            onClick={handleDecline}
            style={{
              padding: "0.5rem 1.25rem",
              fontSize: "0.875rem",
              fontWeight: 500,
              border: "1px solid #d1d5db",
              borderRadius: "0.375rem",
              background: "#fff",
              color: "#374151",
              cursor: "pointer",
            }}
          >
            Decline
          </button>
          <button
            onClick={handleAccept}
            style={{
              padding: "0.5rem 1.25rem",
              fontSize: "0.875rem",
              fontWeight: 600,
              border: "none",
              borderRadius: "0.375rem",
              background: "#10325b",
              color: "#fff",
              cursor: "pointer",
            }}
          >
            Accept
          </button>
        </div>
      </div>
    </div>
  );
}
